import {
  BadgeEuro,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  Camera,
  CarFront,
  Check,
  ClipboardCheck,
  FileText,
  Globe2,
  Home,
  Image,
  LayoutGrid,
  LogOut,
  Mail,
  Plus,
  Search,
  Settings,
  SlidersHorizontal,
  Upload,
  UsersRound,
  WalletCards,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { rankCommands } from "../command-search";
import { saleGuidedListUrl, vehicleGuidedListUrl } from "../guided-actions";
import { useUiPreferences } from "../ui-preferences";

type CommandGroup = "Acciones" | "Ir a" | "Herramientas" | "Administración";

type Command = {
  id: string;
  title: string;
  description: string;
  keywords: string;
  group: CommandGroup;
  icon: LucideIcon;
  to?: string;
  advanced?: boolean;
  adminOnly?: boolean;
  agendaWriteOnly?: boolean;
  searchOnly?: boolean;
  run?: () => void;
};

const RECENTS_KEY = "cochegest:recent-commands";

function readRecentIds(): string[] {
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(RECENTS_KEY) ?? "[]");
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string").slice(0, 5) : [];
  } catch {
    return [];
  }
}

function saveRecentIds(ids: string[]): void {
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(ids.slice(0, 5)));
  } catch {
    // El buscador funciona igualmente sin historial persistente.
  }
}

export function platformShortcut(): string {
  return /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘ K" : "Ctrl K";
}

export function CommandPalette({ open, onClose, onNewVehicle, onLogout }: {
  open: boolean;
  onClose(): void;
  onNewVehicle(): void;
  onLogout(): void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode, setMode } = useUiPreferences();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentIds, setRecentIds] = useState<string[]>(readRecentIds);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const commands = useMemo<Command[]>(() => [
    { id: "account", title: "Mi cuenta y seguridad", description: "Preferencias, doble factor y dispositivos con acceso", keywords: "mi perfil cuenta seguridad contraseña password mfa 2fa qr backup apariencia tema oscuro letra", group: "Ir a", icon: UsersRound, to: "/app/cuenta", searchOnly: true },
    { id: "logout", title: "Cerrar sesión", description: "Salir de Cochegest en este navegador", keywords: "salir logout desconectar cerrar sesion cambiar usuario cuenta", group: "Acciones", icon: LogOut, run: onLogout, searchOnly: true },
    { id: "new-vehicle", title: "Dar de alta un vehículo", description: "Empieza una ficha con matrícula o VIN", keywords: "nuevo coche alta stock matricula vin", group: "Acciones", icon: Plus, run: onNewVehicle },
    { id: "new-client", title: "Añadir un cliente", description: "Crea una oportunidad comercial", keywords: "nuevo lead contacto comprador cliente", group: "Acciones", icon: UsersRound, to: "/app/leads?view=clientes&new=1" },
    { id: "new-sale", title: "Abrir una venta", description: "Vincula vehículo, comprador y operación", keywords: "nueva venta expediente comprador", group: "Acciones", icon: WalletCards, to: "/app/leads?view=ventas&new=1" },
    { id: "new-appraisal", title: "Crear una tasación", description: "Valora una compra o una retoma", keywords: "tasar tasacion retoma compra valorar", group: "Acciones", icon: ClipboardCheck, to: "/app/tasaciones?new=1" },
    { id: "new-supplier", title: "Añadir un proveedor", description: "Registra un particular o una empresa", keywords: "nuevo alta registrar proveedor vendedor particular empresa nif", group: "Acciones", icon: Building2, to: "/app/proveedores?new=1" },
    { id: "new-appointment", title: "Crear una cita", description: "Programa una visita, llegada, ITV, entrega o tarea", keywords: "agenda cita calendario visita cliente llegada itv entrega taller recordatorio", group: "Acciones", icon: CalendarDays, to: "/app/agenda?new=1", agendaWriteOnly: true },
    { id: "register-purchase", title: "Registrar una compra", description: "Elige el vehículo y completa su compra y fiscalidad", keywords: "registrar compra adquisicion proveedor origen fiscal", group: "Acciones", icon: CarFront, to: vehicleGuidedListUrl("purchase"), searchOnly: true },
    { id: "register-cost", title: "Registrar un coste", description: "Elige el vehículo y abre directamente el formulario", keywords: "registrar añadir coste gasto factura taller transporte limpieza garantia", group: "Acciones", icon: BadgeEuro, to: vehicleGuidedListUrl("cost"), searchOnly: true },
    { id: "upload-document", title: "Subir un documento", description: "Elige el vehículo y abre su carpeta documental", keywords: "subir añadir archivar documento contrato ficha permiso factura", group: "Acciones", icon: FileText, to: vehicleGuidedListUrl("document"), searchOnly: true },
    { id: "issue-invoice", title: "Emitir una factura", description: "Elige una venta lista y revisa la emisión", keywords: "emitir crear factura facturar venta", group: "Acciones", icon: FileText, to: saleGuidedListUrl("invoice"), searchOnly: true },
    { id: "deliver-vehicle", title: "Entregar un vehículo", description: "Elige una venta facturada y abre su checklist", keywords: "entregar coche vehiculo acta checklist documentacion", group: "Acciones", icon: Check, to: saleGuidedListUrl("delivery"), searchOnly: true },
    { id: "import-stock", title: "Importar vehículos", description: "Carga un CSV y revisa el origen de cada vehículo", keywords: "importar csv excel archivo stock vehiculos carga masiva", group: "Acciones", icon: Upload, to: "/app/configuracion#empresa", searchOnly: true, adminOnly: true },
    { id: "home", title: "Inicio", description: "Prioridades y trabajo de hoy", keywords: "panel resumen dashboard hoy", group: "Ir a", icon: Home, to: "/app" },
    { id: "agenda", title: "Agenda", description: "Visitas, vehículos, ITV, entregas y taller", keywords: "agenda calendario citas visitas recordatorios equipo", group: "Ir a", icon: CalendarDays, to: "/app/agenda" },
    { id: "vehicles", title: "Vehículos", description: "Stock, estados y fichas", keywords: "stock coches inventario matricula", group: "Ir a", icon: CarFront, to: "/app/stock" },
    { id: "clients", title: "Clientes", description: "Consultas, contactos y seguimiento", keywords: "leads crm embudo oportunidades contactos", group: "Ir a", icon: UsersRound, to: "/app/leads?view=clientes" },
    { id: "sales", title: "Ventas", description: "Expedientes abiertos y entregas", keywords: "operaciones reservas entregas facturas", group: "Ir a", icon: WalletCards, to: "/app/leads?view=ventas" },
    { id: "suppliers", title: "Proveedores", description: "Directorio de particulares y empresas", keywords: "proveedores vendedores nif contacto compras", group: "Ir a", icon: Building2, to: "/app/proveedores", searchOnly: true },
    { id: "appraisals", title: "Tasaciones", description: "Ofertas, recompras y vehículos aceptados", keywords: "tasaciones recompras retomas ofertas valorar historial", group: "Ir a", icon: ClipboardCheck, to: "/app/tasaciones", searchOnly: true },
    { id: "more", title: "Todas las funciones", description: "Tasaciones, taller, fotos, trámites y más, ordenados por objetivo", keywords: "mas herramientas funciones modulos catalogo", group: "Ir a", icon: LayoutGrid, to: "/app/mas" },
    { id: "alerts", title: "Avisos", description: "Todo lo que necesita atención", keywords: "alertas notificaciones pendientes vencimientos", group: "Herramientas", icon: Bell, to: "/app/alertas" },
    { id: "documents", title: "Documentos", description: "Contratos, facturas y caducidades", keywords: "archivos contratos ficha tecnica permiso subir archivar factura compra documento", group: "Herramientas", icon: FileText, to: "/app/documentos" },
    { id: "workshop", title: "Talleres externos", description: "Vehículos fuera, fechas previstas y costes", keywords: "taller externo orden reparacion preparacion coche fuera seguimiento", group: "Herramientas", icon: Wrench, to: "/app/taller" },
    { id: "publishing", title: "Anuncios y portales", description: "Todos los anuncios del stock", keywords: "publicar anuncios conectar anuncios coches net wallapop portales", group: "Herramientas", icon: Globe2, to: "/app/anuncios" },
    { id: "photos", title: "Estudio de fotos", description: "Elige un vehículo y mejora su galería", keywords: "imagenes fotos fotografia subir añadir cargar fondo difuminar editor", group: "Herramientas", icon: Image, to: vehicleGuidedListUrl("photos") },
    { id: "transfers", title: "Trámites", description: "Transferencias y documentación DGT", keywords: "gestoria transferencia dgt cambio titular", group: "Herramientas", icon: FileText, to: "/app/mas?tool=transfers" },
    { id: "communications", title: "Canales de comunicación", description: "Correo y WhatsApp", keywords: "email correo whatsapp automatizacion", group: "Herramientas", icon: Mail, to: "/app/leads?view=canales", advanced: true },
    { id: "intelligence", title: "Inteligencia", description: "Rotación, margen y rendimiento", keywords: "analitica bi datos informes rentabilidad", group: "Herramientas", icon: BarChart3, to: "/app/inteligencia", advanced: true },
    { id: "settings", title: "Configuración", description: "Empresa, equipo y preferencias", keywords: "ajustes cuenta usuarios empresa", group: "Administración", icon: Settings, to: "/app/configuracion", adminOnly: true },
    { id: "fiscal", title: "Configuración fiscal", description: "Sistema de facturación y parámetros", keywords: "rebu iva verifactu sii fiscal impuestos", group: "Administración", icon: BadgeEuro, to: "/app/configuracion#fiscal", advanced: true, adminOnly: true },
    { id: "integrations", title: "Conexiones", description: "Portales, correo y servicios conectados", keywords: "health salud api conexiones observabilidad", group: "Administración", icon: SlidersHorizontal, to: "/app/configuracion#integrations", advanced: true, adminOnly: true },
    { id: "photo-presets", title: "Ajustes de fotografía", description: "Acabados, marca de agua y estilo", keywords: "preset fotografia watermark estudio acabado", group: "Administración", icon: Camera, to: "/app/configuracion#photo-presets", advanced: true, adminOnly: true },
    mode === "essential"
      ? { id: "enable-advanced", title: "Activar modo avanzado", description: "Muestra fiscalidad, conexiones y más controles", keywords: "modo experto avanzado columnas datos integraciones", group: "Administración", icon: SlidersHorizontal, run: () => setMode("advanced") }
      : { id: "enable-essential", title: "Volver al modo esencial", description: "Deja a la vista solo lo necesario para trabajar", keywords: "modo simple sencillo basico esencial", group: "Administración", icon: Check, run: () => setMode("essential") },
  ], [mode, onNewVehicle, onLogout, setMode]);

  const availableCommands = useMemo(() => commands.filter((command) => {
    if (command.advanced && mode !== "advanced") return false;
    if (command.adminOnly && user?.role !== "admin") return false;
    if (command.agendaWriteOnly && user?.role === "external_manager") return false;
    return true;
  }), [commands, mode, user?.role]);

  const results = useMemo(() => {
    if (query.trim()) {
      return rankCommands(availableCommands, query).slice(0, 12);
    }
    const recent = recentIds.flatMap((id) => availableCommands.find((command) => command.id === id) ?? []);
    const rest = availableCommands.filter((command) => !recentIds.includes(command.id) && !command.searchOnly);
    return [...recent, ...rest].slice(0, 12);
  }, [availableCommands, query, recentIds]);

  useEffect(() => { setActiveIndex(0); }, [query, mode]);
  useEffect(() => {
    if (open) dialogRef.current?.querySelector('[role="option"][aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), a[href]")];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  if (!open) return null;

  const execute = (command: Command) => {
    if (command.id !== "logout") {
      const nextRecentIds = [command.id, ...recentIds.filter((id) => id !== command.id)].slice(0, 5);
      setRecentIds(nextRecentIds);
      saveRecentIds(nextRecentIds);
    }
    onClose();
    if (command.run) command.run();
    else if (command.to) navigate(command.to);
  };

  const groupedResults = results.reduce<Array<{ group: CommandGroup; commands: Command[] }>>((groups, command) => {
    const group = groups.find((item) => item.group === command.group);
    if (group) group.commands.push(command);
    else groups.push({ group: command.group, commands: [command] });
    return groups;
  }, []);

  return (
    <div className="command-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div ref={dialogRef} className="command-palette" role="dialog" aria-modal="true" aria-labelledby="command-title">
        <h2 id="command-title" className="sr-only">Buscar funciones de Cochegest</h2>
        <div className="command-search">
          <Search aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") { event.preventDefault(); setActiveIndex((index) => Math.min(results.length - 1, index + 1)); }
              if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 1)); }
              if (event.key === "Enter" && results[activeIndex]) { event.preventDefault(); execute(results[activeIndex]); }
            }}
            role="combobox"
            aria-controls="command-results"
            aria-expanded="true"
            aria-autocomplete="list"
            aria-activedescendant={results[activeIndex] ? `command-${results[activeIndex].id}` : undefined}
            placeholder="¿Qué quieres hacer?"
          />
          {query ? <button type="button" onClick={() => setQuery("")} aria-label="Borrar búsqueda"><X /></button> : <kbd>{platformShortcut()}</kbd>}
        </div>
        <div className="command-context">
          <span aria-live="polite">{query ? `${results.length} ${results.length === 1 ? "coincidencia" : "coincidencias"}` : recentIds.length ? "Recientes y sugerencias" : "Sugerencias"}</span>
          <span className={`interface-mode-chip interface-mode-chip--${mode}`}>{mode === "essential" ? "Modo esencial" : "Modo avanzado"}</span>
        </div>
        <div id="command-results" className="command-results" role="listbox">
          {groupedResults.map((group) => (
            <section key={group.group} aria-label={group.group}>
              <h3>{group.group}</h3>
              {group.commands.map((command) => {
                const Icon = command.icon;
                const index = results.findIndex((item) => item.id === command.id);
                return <button
                  id={`command-${command.id}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={index === activeIndex ? "is-active" : ""}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => execute(command)}
                  key={command.id}
                >
                  <span><Icon /></span>
                  <div><strong>{command.title}</strong><small>{command.description}</small></div>
                  {command.advanced && <i>Avanzado</i>}
                </button>;
              })}
            </section>
          ))}
          {!results.length && <div className="command-empty"><Search /><strong>No encontramos esa función</strong><p>Prueba con una tarea como “subir fotos”, “registrar un coste” o “emitir una factura”.</p><button type="button" className="button button--secondary" onClick={() => setQuery("")}>Ver todas las funciones</button></div>}
        </div>
        <footer><span><kbd>↑</kbd><kbd>↓</kbd> Mover</span><span><kbd>↵</kbd> Abrir</span><span><kbd>Esc</kbd> Cerrar</span></footer>
      </div>
    </div>
  );
}
